/* eslint-disable @typescript-eslint/no-shadow */

import React, { memo, useState } from "react";

import styled from "styled-components";

import {
  Box,
  Checkbox,
  CheckboxProps,
  Column,
  Columns,
  Divider,
  Stack,
  Text,
} from "@origin-digital/ods-core";

import {
  getWeekInString,
  getYearInString,
} from "@/features/ServiceProviderSelection/TimeUnitRenderer";
import { RatingRenderer } from "@/features/ServiceProviderSelection/RatingRenderer";
import * as content from "./content.json";
import { formatNumberWithCommas } from "@/utils";
import { isBatteryJourney } from "@/features/SolarForm/utils/formJourney";
import { useFormContext } from "react-hook-form";

type InstallerCheckboxProps = CheckboxProps & {
  providerDetail: any;
};

const CheckboxContainer = styled.div(() => ({
  "&": {
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #E3E3E3",
    margin: "6px",
    width: "300px",

    "&.checked": {
      borderColor: "#FFB432",
    },

    "& label": {
      marginLeft: 0,
      marginRight: 0,
      width: "100%",
      height: "100%",
    },
  },
}));

const LabeledText = styled(Text)(() => ({
  "&&": {
    color: "#737373",
    fontSize: "12px",
  },
}));

export const InstallerCheckbox = memo((props: InstallerCheckboxProps) => {
  const [checked, setChecked] = useState(props.checked);
  const { getValues } = useFormContext();
  const systemType = getValues("systemType");

  const handleOnChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setChecked(checked);
    if (props.onChange) {
      props.onChange(event, checked);
    }
  };

  const formatPrice = (minPrice: number, maxPrice: number) => {
    return `$${formatNumberWithCommas(minPrice)} -$${formatNumberWithCommas(
      maxPrice
    )}`;
  };

  const formatWhyUs = () => {
    const formatEachItem = props.providerDetail.whyUs.map((item: string) =>
      item.endsWith(".") ? `${item} ` : `${item}. `
    );

    let fullSentence = "";
    formatEachItem.forEach((item: string) => {
      if (fullSentence.length + item.length < 155) {
        fullSentence = fullSentence + item;
      }
    });

    return fullSentence;
  };

  const {
    minPrice,
    maxPrice,
    workmanshipWarranty,
    yearsInBusiness,
    installTurnaroundTime,
    financeDuration,
    overallRating,
    whyUs,
  } = props.providerDetail;

  return (
    <CheckboxContainer className={checked ? "checked" : "unchecked"}>
      <Box margin="xxsmall">
        <Checkbox
          className="custom-checkbox"
          {...props}
          checked={checked}
          onChange={handleOnChange}
        />
      </Box>
      <Divider />
      <Box padding="medium">
        {minPrice !== undefined &&
          maxPrice !== undefined &&
          !isBatteryJourney(systemType) && (
            <Box marginBottom="medium">
              <Text tone="neutralDark" variant="subtitle-small" weight="medium">
                {formatPrice(minPrice, maxPrice)}
              </Text>
            </Box>
          )}
        {whyUs && whyUs.length > 0 && (
          <Box marginBottom="large">
            <Text variant="body-small">{formatWhyUs()}</Text>
          </Box>
        )}
        <Stack space="small">
          <Column>
            <LabeledText>{content.workmanShipWarranty}</LabeledText>
            <Text tone="neutralDark">
              {getYearInString(workmanshipWarranty)}
            </Text>
          </Column>
          <Columns space="small">
            <Column>
              <LabeledText>{content.yearsInBusiness}</LabeledText>
              <Text tone="neutralDark">{getYearInString(yearsInBusiness)}</Text>
            </Column>
            <Column>
              <LabeledText>{content.turnAroundTime}</LabeledText>
              <Text tone="neutralDark">
                {getWeekInString(installTurnaroundTime)}
              </Text>
            </Column>
          </Columns>
          <Columns space="medium">
            <Column>
              <LabeledText>{content.financeDuration}</LabeledText>
              <Text tone="neutralDark">{getYearInString(financeDuration)}</Text>
            </Column>
            <Column>
              <LabeledText>{content.overallRating}</LabeledText>
              <Text tone="neutralDark">
                {RatingRenderer(+overallRating, props.id + overallRating, {
                  upMd: false,
                })}
              </Text>
            </Column>
          </Columns>
        </Stack>
      </Box>
    </CheckboxContainer>
  );
});
